import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPostById, updatePost } from '../../services/postService';
import { useAuth } from '../../context/AuthContext';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { TextField, Button, Typography, Container, Paper, Box, CircularProgress, Alert } from '@mui/material';

const EditPost = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { firestoreUser } = useAuth();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [originalPost, setOriginalPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchPost = async () => {
      if (!firestoreUser) {
        setError('You must be logged in to edit a post.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const postData = await getPostById(postId);
        if (postData) {
          // Check if the current user is an admin or the author of the post
          const isAdmin = firestoreUser.roles && firestoreUser.roles.admin;
          const isAuthor = postData.userId === firestoreUser.uid;

          if (!isAdmin && !isAuthor) {
            setError('You do not have permission to edit this post.');
            setLoading(false);
            return;
          }
          setOriginalPost(postData);
          setTitle(postData.title);
          setContent(postData.content);
        } else {
          setError('Post not found.');
        }
      } catch (err) {
        console.error('Error fetching post for editing:', err);
        setError('Failed to load post data. ' + err.message);
      }
      setLoading(false);
    };

    fetchPost();
  }, [postId, firestoreUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!title.trim() || !content.trim()) {
      setError('Title and content cannot be empty.');
      return;
    }

    if (!firestoreUser) {
      setError('Authentication error. Please log in again.');
      return;
    }

    setSubmitting(true);
    try {
      await updatePost(postId, { title, content }, firestoreUser.uid);
      setSuccess('Post updated successfully!');
      setTimeout(() => {
        navigate(`/post/${postId}`);
      }, 1500);
    } catch (err) {
      console.error('Error updating post:', err);
      setError('Failed to update post. ' + err.message);
    }
    setSubmitting(false);
  };

  const quillModules = {
    toolbar: [
      [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
      [{size: []}],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}, 
       {'indent': '-1'}, {'indent': '+1'}],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !originalPost) { // Show error prominently if post couldn't be loaded for editing
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>Go Back</Button>
      </Container>
    );
  }
  
  // If there's an error but we have originalPost, it means the error is from submission or a non-blocking issue.
  // We still want to show the form in that case.

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, backgroundColor: '#f5f5dc', color: '#5c3c10' }}>        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#8b4513', fontFamily: 'Georgia, serif', textAlign: 'center' }}>
          Edit Conference Clamp Notice: {originalPost?.title}
        </Typography>
        <form onSubmit={handleSubmit}>
          <Box mb={3}>
            <TextField
              label="Post Title"
              variant="outlined"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              sx={{ 
                backgroundColor: '#fffacd', 
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#d2b48c' },
                  '&:hover fieldset': { borderColor: '#b8860b' },
                }
              }}
              InputLabelProps={{ sx: { color: '#5c3c10' } }}
              inputProps={{ sx: { color: '#5c3c10' } }}
            />
          </Box>
          <Box mb={3} sx={{ backgroundColor: '#fffacd', border: '1px solid #d2b48c', borderRadius: '4px' }}>
            <ReactQuill 
              theme="snow" 
              value={content}
              onChange={setContent}
              modules={quillModules}
              style={{ color: '#5c3c10' }} // Base text color for editor content
            />
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button 
              type="button" 
              variant="outlined" 
              onClick={() => navigate(originalPost ? `/post/${postId}` : '/town-square')}
              disabled={submitting}
              sx={{ borderColor: '#5c3c10', color: '#5c3c10', '&:hover': { borderColor: '#8b4513', backgroundColor: 'rgba(139, 69, 19, 0.04)' } }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={submitting || !title.trim() || !content.trim()}
              sx={{ backgroundColor: '#d98d2a', color: '#F5F5F5', '&:hover': { backgroundColor: '#c77a1a' } }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default EditPost;
